import React, { useEffect, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '@/config/api';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import useOrdersPolling from '@/hooks/useOrdersPolling';

const API = API_BASE_URL;
const notificationSound = new Audio("/sounds/bell.wav");

const Kitchen = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const scopeKey = JSON.stringify([user?.id, user?.role, user?.company_id, user?.restaurant_id]);
  const { orders, hasLoaded, initialLoading, initialError, refreshError, refresh, refreshAfterMutation } =
    useOrdersPolling({ apiBaseUrl: API, scopeKey });
  const observed = useRef({ scopeKey, initialized: false, ids: new Set() });

  useEffect(() => {
    if (observed.current.scopeKey !== scopeKey) {
      observed.current = { scopeKey, initialized: false, ids: new Set() };
    }
    if (!hasLoaded) return;
    const baseline = observed.current;
    const shouldAlert = baseline.initialized &&
      orders.some(order => !baseline.ids.has(order.id) && order.status === 'new');
    orders.forEach(order => baseline.ids.add(order.id));
    baseline.initialized = true;
    if (shouldAlert) {
      try {
        notificationSound.currentTime = 0;
        Promise.resolve(notificationSound.play()).catch(console.error);
      } catch (error) {
        console.error(error);
      }
    }
  }, [orders, hasLoaded, scopeKey]);

  const updateStatus = async (orderId, newStatus) => {
    try {
      await axios.patch(`${API}/orders/${orderId}/status`, { status: newStatus }, { withCredentials: true });
      toast.success('Order moved');
      refreshAfterMutation();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const columns = [
    { status: 'new', title: t('newOrders'), color: '#3B82F6', next: 'preparing' },
    { status: 'preparing', title: t('preparing'), color: '#F59E0B', next: 'ready' },
    { status: 'ready', title: t('ready'), color: '#10B981', next: 'completed' },
    { status: 'completed', title: t('completed'), color: '#64748B', next: null },
  ];

  return (
    <div className="space-y-6" data-testid="kitchen-page">
      <h1 className="text-4xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif', color: '#232D4B' }}>
        {t('kitchen')}
      </h1>

      {initialError && <div role="alert">Unable to load orders. <Button onClick={refresh}>Retry</Button></div>}
      {refreshError && <div role="status">Updates delayed - retrying. <Button onClick={refresh}>Retry</Button></div>}
      {initialLoading && <p role="status">Loading orders...</p>}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {columns.map(col => {
          const colOrders = orders.filter(o => o.status === col.status);
          return (
            <div key={col.status} data-testid={`kitchen-column-${col.status}`}>
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-semibold" style={{ fontFamily: 'Outfit, sans-serif', color: col.color }}>
                  {col.title}
                </h2>
                <Badge style={{ backgroundColor: col.color, color: 'white' }}>{colOrders.length}</Badge>
              </div>
              <div className="space-y-3 min-h-[200px]">
                {colOrders.map(order => (
                  <Card key={order.id} className="border-s-4" style={{ borderInlineStartColor: col.color }} data-testid={`kitchen-order-${order.id}`}>
                    <CardHeader className="pb-2">
<CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
  {order.order_number}
</CardTitle>

<p className="text-xs text-gray-500">
⏱ {order.status === "completed" && order.completed_at
  ? `Completed in ${Math.floor(
      (new Date(order.completed_at + "Z").getTime() -
       new Date(order.created_at + "Z").getTime()) / 60000
    )} min`
  : `${Math.floor(
      (Date.now() -
       new Date(order.created_at + "Z").getTime()) / 60000
    )} min`}
  </p>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div className="text-sm space-y-1">
                        {order.items?.map((item, idx) => (
                          <div key={idx} className="flex justify-between">
                            <span>{item.name}</span>
                            <span className="font-bold">x{item.quantity}</span>
                          </div>
                        ))}
                      </div>
                      <div className="font-bold text-lg" style={{ color: '#E57200', fontFamily: 'Outfit, sans-serif' }}>
                        RM{order.total_amount.toFixed(2)}
                      </div>
                      {col.next && (
                        <Button
                          size="sm"
                          className="w-full"
                          style={{ backgroundColor: '#232D4B' }}
                          onClick={() => updateStatus(order.id, col.next)}
                          data-testid={`move-to-${col.next}-${order.id}`}
                        >
                          Move to {col.next}
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Kitchen;
